import * as Sentry from '@sentry/nextjs';
import addBusinessDays from 'date-fns/addBusinessDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import getMonth from 'date-fns/getMonth';
import getYear from 'date-fns/getYear';
import isWeekend from 'date-fns/isWeekend';
import isWithinInterval from 'date-fns/isWithinInterval';
import parse from 'date-fns/parse';
import type { Extras } from '@sentry/types';
import type { ObjectSchema } from 'yup';

import Yup, { type YupI18nError } from '../validation.service';

import {
  CancellationReason,
  CancellationType,
  ETFormLayout,
  ETFormVariant,
  Step,
  TerminationDateType,
} from './utils/const';
import * as validationDE from './validation/de';
import * as validationFR from './validation/fr';
import germanHolidaysRaw from './validation/assets/german-holidays';
import type { Theme } from './utils/types';
export * from './utils/const';
export * from './utils/types';

/**
 * Validates termination date and throws if it's invalid
 */
export function validateTerminationDate(
  date: Date | undefined,
  terminationDateType: TerminationDateType,
  cancellationReason: CancellationReason
): void {
  if (terminationDateType === TerminationDateType.AsSoonAsPossible) {
    // This is fine, we don't validate the date in this case
    return;
  }

  if (!date) {
    throw { message: (params: Record<string, unknown>) => ({ key: 'mixed.required', params } as YupI18nError) };
  }

  const daysDiff = differenceInCalendarDays(new Date(), date);
  if (cancellationReason === CancellationReason.CustomerRelocation) {
    if (daysDiff > 5 * 7) {
      const error = new Error(`The date can be a maximum of five weeks in the past, daysDiff: ${daysDiff}`);
      const extraData: Extras = { daysDiff };
      Sentry.captureException(error, { extra: extraData });
      throw error;
    }
  } else {
    if (daysDiff >= 0) {
      const error = new Error(`The date must be in the future, daysDiff: ${daysDiff}`);
      const extraData: Extras = { daysDiff };
      Sentry.captureException(error, { extra: extraData });
      throw error;
    }
  }
}

export const DateInputFormat = 'yyyy-MM-dd' as const;
export const DateDefaultFormat = 'dd.MM.yyyy' as const;

/**
 * A list of all German holidays
 */
const germanHolidays = germanHolidaysRaw.map(dateString => parse(dateString, 'dd.MM.yyyy', new Date()));

/**
 * Adds a specific amount of business days, taking public German holidays into consideration.
 *
 * Data is valid until 2030.
 */
export function addBusinessDaysWithGermanHolidays(date: Date, amount: number) {
  if (getYear(date) === 2031 || (getYear(date) === 2030 && getMonth(date) === 11)) {
    const error = new Error('German holiday data is out of date!');
    const extraData: Extras = { date, amountOfBusinessDays: amount };
    Sentry.captureException(error, { extra: extraData });
    throw error;
  }

  let endDate = addBusinessDays(date, amount);
  const daysChecked: Set<string> = new Set();

  /**
   * Recursively check for conflicting holidays and move the end date until these are calculated into the end date.
   */
  function checkForHolidays() {
    const matchingHolidays = germanHolidays
      .filter(holiday => isWithinInterval(holiday, { start: date, end: endDate }))
      // Filter out any dates that would fall on a weekend, as those have no impact on the calculation
      .filter(holiday => isWeekend(holiday) === false)
      // Avoid counting holidays more than once
      .filter(holiday => !daysChecked.has(holiday.toISOString()));

    matchingHolidays.forEach(holiday => daysChecked.add(holiday.toISOString()));

    if (matchingHolidays.length > 0) {
      // We found a holiday that needs to be considered, move the end date and check again
      endDate = addBusinessDays(endDate, matchingHolidays.length);
      checkForHolidays();
    }
  }
  checkForHolidays();

  return endDate;
}

export const ETCancellationFormValidation = Yup.object({
  firstName: Yup.string().max(70).required(),
  lastName: Yup.string().max(70).required(),
  zipCode: Yup.string().trim().length(5).required(),
  city: Yup.string().required(),
  street: Yup.string().max(70).required(),
  streetNumber: Yup.string().max(10).required(),
  birthDay: Yup.date().required(),
  email: Yup.string().email().required(),
  /**
   * Contract id, we are using the internal name of GETEC here for simplicity
   */
  plantKey: Yup.string().trim().matches(/^\d+$/, 'Die Vertragsnummer besteht nur aus Ziffern').required(),
  cancellationType: Yup.string().oneOf(Object.values(CancellationType)).required(),
  cancellationReason: Yup.string().oneOf(Object.values(CancellationReason)).required(),
  terminationDateType: Yup.string().oneOf(Object.values(TerminationDateType)).required(),
  terminationDate: Yup.mixed().test(function (date?: Date) {
    try {
      validateTerminationDate(date, this.parent.terminationDateType, this.parent.cancellationReason);
      return true;
    } catch (e) {
      return this.createError({ message: (e as Error).message });
    }
  }),
  // gRecaptchaResponse: Yup.string().required(),
});

export type ETCancellationFormValues = Yup.InferType<typeof ETCancellationFormValidation>;

export const ETCancellationFormInitialValues: ETCancellationFormValues = {
  firstName: '',
  lastName: '',
  zipCode: '',
  city: '',
  street: '',
  streetNumber: '',
  // @ts-expect-error (has to be empty string to display initial state)
  birthDay: '',
  email: '',
  plantKey: '',
  cancellationType: CancellationType.Ordinary,
  cancellationReason: '',
  terminationDateType: TerminationDateType.AsSoonAsPossible,
  terminationDate: '',
  // gRecaptchaResponse: '',
};

export type Values = validationDE.ValuesDE | validationFR.ValuesFR;

export function isLayoutDE(values: Values): values is validationDE.ValuesDE {
  return (values as validationDE.ValuesDE)._layout === ETFormLayout.DE;
}

export function isVariantEyond(values: Values): values is validationDE.ValuesDE {
  return (values as validationDE.ValuesDE)._variant === ETFormVariant.TMH;
}

export function isVariantRenaultFR(values: Values): values is validationFR.ValuesFR {
  return (values as validationFR.ValuesFR)._variant === ETFormVariant.RenaultFR;
}

export interface YupFormConfig {
  initialValues: Values;
  validations: Record<Step, ObjectSchema<any>>;
}

type ValidationOptions = Parameters<typeof validationDE.getValidations>[1] &
  Parameters<typeof validationFR.getValidations>[1];

const variations: Record<
  ETFormLayout,
  {
    getValidations: (theme: Theme, options: any) => Record<Step, ObjectSchema<any>>;
    getInitialValues: (theme: Theme) => Values;
  }
> = {
  [ETFormLayout.DE]: validationDE,
  [ETFormLayout.FR]: validationFR,
};

export const etFormVariantToLayoutMap: Record<ETFormVariant, ETFormLayout> = {
  [ETFormVariant.Mercedes]: ETFormLayout.DE,
  [ETFormVariant.RenaultDE]: ETFormLayout.DE,
  [ETFormVariant.RenaultFR]: ETFormLayout.FR,
  [ETFormVariant.TMH]: ETFormLayout.DE,
};

export function getYupFormConfig(theme: Theme, options: { validations: ValidationOptions }): YupFormConfig {
  const variation = variations[etFormVariantToLayoutMap[theme.variant]];

  return {
    initialValues: variation.getInitialValues(theme),
    validations: variation.getValidations(theme, options.validations),
  };
}

export interface ETFormConfig {
  consumptionCalculator: {
    homeConnectionHours: boolean;
  };
  useFlexCoins: boolean;
  useSmartMeter: boolean;
  tariffs: {
    multipleTariffs: boolean;
  };
  review: {
    acceptPowerDeliveryBeforeRevocation: boolean;
    existingHardware: boolean;
  };
  useSSO: boolean;
}

export const etFormConfig: Record<ETFormVariant, ETFormConfig> = {
  [ETFormVariant.TMH]: {
    consumptionCalculator: {
      homeConnectionHours: false,
    },
    useFlexCoins: true,
    useSmartMeter: true,
    tariffs: {
      multipleTariffs: true,
    },
    review: {
      acceptPowerDeliveryBeforeRevocation: true,
      existingHardware: true,
    },
    useSSO: false,
  },
  [ETFormVariant.RenaultDE]: {
    consumptionCalculator: {
      homeConnectionHours: false,
    },
    useFlexCoins: false,
    useSmartMeter: false,
    tariffs: {
      multipleTariffs: false,
    },
    review: {
      acceptPowerDeliveryBeforeRevocation: true,
      existingHardware: false,
    },
    useSSO: true,
  },
  [ETFormVariant.RenaultFR]: {
    consumptionCalculator: {
      homeConnectionHours: true,
    },
    useFlexCoins: true,
    useSmartMeter: false,
    tariffs: {
      multipleTariffs: false,
    },
    review: {
      acceptPowerDeliveryBeforeRevocation: false,
      existingHardware: false,
    },
    useSSO: true,
  },
  // TODO: edit it when working with Theme
  [ETFormVariant.Mercedes]: {
    consumptionCalculator: {
      homeConnectionHours: false,
    },
    useFlexCoins: true,
    useSmartMeter: true,
    tariffs: {
      multipleTariffs: true,
    },
    review: {
      acceptPowerDeliveryBeforeRevocation: true,
      existingHardware: true,
    },
    useSSO: false,
  },
};

export interface LocationProviderResponse {
  grundversorgerId: number | null;
  netzbetreiberId: number | null;
}

interface PrefillQueryParam {
  activeForm: 'calculator' | 'exactValue';
  carConsumptionFormValues: {
    mileage: number;
    consumption: number;
    yearlyConsumption: number;
    chargeHome: number;
    vehicleSelection: number | undefined;
    homeConnectionHours: number | undefined;
  };
  exactValueFormValues: {
    carConsumption: number;
    homeConnectionHours: number;
  };
  householdFormValues: {
    zipCode: string;
    householdConsumption: number;
  };
}

export const PrefillQueryParamName = 'prefill';
export const EmailDoiStateTokenQueryParamName = 'emailDoiFlowStateToken';

export function encodePrefillQueryParam(params: PrefillQueryParam) {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.btoa(JSON.stringify(params));
}

export function getPrefillQueryParam(): PrefillQueryParam | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const query = new URLSearchParams(window.location.search);
  const prefill = query.get(PrefillQueryParamName);
  if (!prefill) {
    return null;
  }
  try {
    return JSON.parse(window.atob(prefill)) as PrefillQueryParam;
  } catch (e) {
    return null;
  }
}

export function getEmailDoiFlowStateToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const query = new URLSearchParams(window.location.search);
  return query.get(EmailDoiStateTokenQueryParamName);
}

export const check24CSVColumns = [
  'values.0.zipCode',
  'values.0.city',
  'values.0.householdConsumption',
  'values.0.carConsumption',
  'values.2.salutation',
  'values.2.title',
  'values.2.firstName',
  'values.2.lastName',
  'values.2.street',
  'values.2.streetNumber',
  'values.2.differentBillingAddress',
  'values.2.billingSalutation',
  'values.2.billingTitle',
  'values.2.billingFirstName',
  'values.2.billingLastName',
  'values.2.billingZipCode',
  'values.2.billingCity',
  'values.2.billingStreet',
  'values.2.billingStreetNumber',
  'values.2.email',
  'values.2.phone',
  'values.2.birthDay',
  'values.2.reason',
  'values.2.termination',
  'values.2.terminationDate',
  'values.2.currentProvider',
  'values.2.currentProviderCustomerId',
  'values.2.desiredDeliveryDate',
  'values.2.meterNumber',
  'values.2.marketLocationId',
  'values.2.paymentMethod',
  'values.2.iban',
  'values.2.differentAccountHolder',
  'values.2.accountHolderName',
  'values.2.acceptSepaDirectDebit',
  'values.3.acceptTermsAndPrivacy',
  'values.3.acceptPowerDeliveryBeforeRevocationEnds',
  'values.3.flexCoinValueAndSmartMeter',
  'values.3.smartMeterExisting',
  'values.3.smartMeterInterest',
  'tariff.id',
  'tariff.name',
  'tariff.minimumContractDuration',
  'tariff.minimumContractDurationId',
  'tariff.cancellationPeriod',
  'tariff.cancellationPeriodId',
  'tariff.priceGuaranteeDuration',
  'tariff.grundpreisBrutto',
  'tariff.grundpreisNetto',
  'tariff.arbeitspreisHTCtKWhBrutto',
  'tariff.arbeitspreisHTCtKWhNetto',
  'tariff.gesamtpreisBrutto',
  'tariff.gesamtpreisProMonatBrutto',
  'host',
  'locale',
  'additionalData.valuePerFlexcoin',
  'additionalData.submitDate',
  'additionalData.externalLeadReferenceId',
] as const;

export const check24CSVExampleValues: Record<(typeof check24CSVColumns)[number], string> = {
  'values.0.zipCode': '01067',
  'values.0.city': 'Dresden',
  'values.0.householdConsumption': '2500',
  'values.0.carConsumption': '0',
  'values.2.salutation': 'Herr',
  'values.2.title': '',
  'values.2.firstName': 'Max',
  'values.2.lastName': 'Mustermann',
  'values.2.street': 'Bernhard-von-Lindenau-Platz',
  'values.2.streetNumber': '16a',
  'values.2.differentBillingAddress': 'WAHR',
  'values.2.billingSalutation': 'Herr',
  'values.2.billingTitle': '',
  'values.2.billingFirstName': 'Max',
  'values.2.billingLastName': 'Mustermann',
  'values.2.billingZipCode': '68161',
  'values.2.billingCity': 'Mannheim',
  'values.2.billingStreet': 'Mustermann',
  'values.2.billingStreetNumber': '16a',
  'values.2.email': 'Robert.Zech+test@check24.de',
  'values.2.phone': '',
  'values.2.birthDay': '1984-01-01',
  'values.2.reason': 'Switch',
  'values.2.termination': 'TMH',
  'values.2.terminationDate': '',
  'values.2.currentProvider': '9978012000006',
  'values.2.currentProviderCustomerId': '6431231312',
  'values.2.desiredDeliveryDate': '2024-03-15',
  'values.2.meterNumber': '543512121',
  'values.2.marketLocationId': '41373559241',
  'values.2.paymentMethod': 'DirectDebit',
  'values.2.iban': 'AT021200000703447144',
  'values.2.differentAccountHolder': '',
  'values.2.accountHolderName': 'Max Mustermann',
  'values.2.acceptSepaDirectDebit': 'WAHR',
  'values.3.acceptTermsAndPrivacy': 'WAHR',
  'values.3.acceptPowerDeliveryBeforeRevocationEnds': 'WAHR',
  'values.3.flexCoinValueAndSmartMeter': 'WAHR',
  'values.3.smartMeterExisting': '',
  'values.3.smartMeterInterest': '',
  'tariff.id': '775375',
  'tariff.name': 'The Mobility House, Öko (Smart) 12',
  'tariff.minimumContractDuration': '12 Monate',
  'tariff.minimumContractDurationId': '2014',
  'tariff.cancellationPeriod': '4 Wochen zum Vertragsende',
  'tariff.cancellationPeriodId': '1902',
  'tariff.priceGuaranteeDuration': '12',
  'tariff.grundpreisBrutto': '11163.00',
  'tariff.grundpreisNetto': '9380.67',
  'tariff.arbeitspreisHTCtKWhBrutto': '32.28',
  'tariff.arbeitspreisHTCtKWhNetto': '27.13',
  'tariff.gesamtpreisBrutto': '91863.00',
  'tariff.gesamtpreisProMonatBrutto': '7655.25',
  host: 'eyond.mobilityhouse.com',
  locale: 'de',
  'additionalData.valuePerFlexcoin': '10,00',
  'additionalData.submitDate': '2024-02-14',
  'additionalData.externalLeadReferenceId': '21236980',
};
